import React from "react";
import { type NextPage } from "next";
import { Box, Flex } from "@chakra-ui/react";
import { HalfHero } from "components/half-hero";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { CenteredQuote } from "components/quotes";
import { Link } from "ui/link";
import { StackedLogos } from "components/logos";
import { getLandingPageProps, getPageMetaImage, type LandingPageProps } from "utils";
import { Diptych } from "ui/diptych";
import Image from "next/image";
import { Button } from "ui/button";
import heroImage from "../../../public/assets/platform/observability/hero.png";
import pair1Image from "../../../public/assets/platform/observability/pair-1.svg";
import pair2Image from "../../../public/assets/platform/observability/pair-2.svg";
import pair3Image from "../../../public/assets/platform/observability/pair-3.svg";
import pair4Image from "../../../public/assets/platform/observability/pair-4.svg";

const Observability: NextPage<LandingPageProps> = ({ page, announcementBanner, defaultLogos }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      footerOptions={{ variant: "blue" }}
    >
      <HalfHero
        pt="big"
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        image={heroImage}
        imageRatio={3 / 2}
        imageObjectFit="contain"
      />

      <StackedLogos customers={defaultLogos} />

      <Section>
        <Flex flexDir="column" gap={{ base: "huge", md: "hugest" }}>
          <Diptych
            direction="right"
            mediaRatio={492 / 356}
            media={
              <Box p="small">
                <Image src={pair1Image} alt="An illustration representing Salesforce syncs for three separate users." />
              </Box>
            }
            chip={{ text: "Live Debugger" }}
            title="Find and fix errors fast"
            description="Dive deeper into failed runs with full API requests and responses. Filter and search across the entirety of synced rows, without sampling, to quickly identify problematic data. Debugger data is exportable to CSV for adhoc analysis."
            actions={
              <Button hasRightChevron href="/blog/introducing-the-live-debugger">
                Learn more
              </Button>
            }
          />

          <Diptych
            direction="left"
            mediaRatio={527 / 485}
            media={
              <Box p="small">
                <Image
                  src={pair2Image}
                  alt="An abstract illustration of a SQL query used to sync data from Snowflake."
                />
              </Box>
            }
            chip={{ text: "Sync logs" }}
            title="Analyze logs from your syncs directly in the warehouse"
            description="Write all of the results of your syncs back to your warehouse so you can investigate the status of each sync with the full flexibility of SQL and in the context of all of your customer data. Because we know all of your data belongs together."
            actions={
              <Button hasRightChevron href="/blog/introducing-sync-logs">
                Learn more
              </Button>
            }
          />

          <Diptych
            direction="right"
            mediaRatio={545 / 521}
            media={
              <Box p="small">
                <Image
                  src={pair3Image}
                  alt="An abstract illustration of a Slack notification from the Hightouch app."
                />
              </Box>
            }
            chip={{ text: "Configurable alerts" }}
            title="Customize how, when, and where you get notified"
            description="Get ahead of potential data pipeline issues before your internal customers notice. Define the number of row failures that merit an alerting event, group errors to stave off alert fatigue, and get notified in your channel of choice—whether that’s Slack, email, or PagerDuty."
            actions={
              <Button hasRightChevron href="/blog/why-your-alerting-should-be-configurable">
                Learn more
              </Button>
            }
          />

          <Diptych
            direction="left"
            mediaRatio={527 / 485}
            media={
              <Box p="small">
                <Image src={pair4Image} alt="An abstract illustration of log data in Datadog." />
              </Box>
            }
            chip={{ text: "Datadog integration" }}
            title="Unify your observability approach"
            description="Configure fine-grained alerts based on your customized thresholds, or get proactively notified when anomalous behavior is detected. You can also build custom dashboards in Datadog to better understand sync performance and trends."
            actions={
              <Button hasRightChevron href="/blog/customized-alerts-datadog-integration">
                Learn more
              </Button>
            }
          />
        </Flex>
      </Section>

      <Section my={0} pb={sectionSpacingY}>
        <CenteredQuote
          maxW="1100px"
          author={{
            name: "Erik Edelmann",
            job: "Sr. Analytics Engineering Manager",
          }}
          company={{
            name: "Vendr",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/eaffd1ff20eacedf96184b2c461c5ef67c8c1b2e-500x125.png",
          }}
          quoteText={
            <>
              Our team implements Hightouch in some of our most critical workflows, so it’s important for us to be able
              to make sure our models and syncs are working as expected. We can count on{" "}
              <Link href="/blog/why-your-alerting-should-be-configurable" variant="inlineDark">
                Hightouch Alerts
              </Link>{" "}
              to notify us right away in Slack when something unexpected happens in one of our syncs, and when something
              does, we can depend on the{" "}
              <Link href="/blog/introducing-the-live-debugger" variant="inlineDark">
                Live Debugger
              </Link>{" "}
              to help us get to the bottom of the issue quickly and efficiently.
            </>
          }
          cta={{ href: "/customers/vendr", text: "Read customer story" }}
        />
      </Section>
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/platform/observability", {
  loadDefaultLogos: true,
});

export default Observability;
